import {
  Callout,
  Classes,
  FormGroup,
  H5,
  InputGroup,
  Intent,
  Spinner,
  SpinnerSize,
} from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { useDropzone } from 'react-dropzone';
import { Link, Text } from 'vet-bones/components';

import { AdvancedOptions } from 'src/components/AdvancedOptions';
import { FileExtension } from 'src/constants/fileExtensions';
import { MAX_CSV_VISIBLE_LINES } from 'src/constants/limits';
import { TEST_IDS } from 'src/constants/testIds';
import { ProjectFileUploadOptionsForm } from 'src/containers/ProjectFileUploadPanel/ProjectFileUploadOptionsForm';
import { ProjectFileUploadTable } from 'src/containers/ProjectFileUploadPanel/ProjectFileUploadTable';
import {
  ProjectFileUploadField,
  ProjectFileUploadOptions,
} from 'src/store/state/projects';
import * as copy from 'src/templates/copy';
import { ExtendedParseError } from 'src/utils/csv/parseCsv';

export type ProjectFileUploadPreviewPanelProps = {
  errors: ExtendedParseError[];
  fields: ProjectFileUploadField[];
  fileName: string;
  fileOptions: ProjectFileUploadOptions;
  filePath: string;
  isFilePartial: boolean;
  isParsing: boolean;
  isThereDuplicateHeaders: boolean;
  onFileUpload: (files: File[]) => void;
  rows: string[][];
  warningText: string;
  setFileName: (fileName: string) => void;
  setFileOptions: (fileOptions: ProjectFileUploadOptions) => void;
  updateFieldLabel: (value: string, label: string) => void;
};

export const ProjectFileUploadPreviewPanel: React.VFC<
  ProjectFileUploadPreviewPanelProps
> = ({
  errors,
  isFilePartial,
  fields,
  fileOptions,
  fileName,
  filePath,
  isParsing,
  isThereDuplicateHeaders,
  onFileUpload,
  rows,
  warningText,
  setFileName,
  setFileOptions,
  updateFieldLabel,
}) => {
  const { getInputProps, open } = useDropzone({
    accept: [FileExtension.CSV],
    multiple: false,
    noClick: true,
    noKeyboard: true,
    onDropAccepted: onFileUpload,
  });

  let preview: JSX.Element | undefined;
  let visibleRows = [];
  if (isParsing) {
    preview = <Spinner size={SpinnerSize.LARGE} />;
  } else if (!fileOptions.separator) {
    preview = (
      <Callout
        intent={Intent.DANGER}
        title={
          copy.components.projectResourceDialog.projectFileUploadPanel
            .validation.separator
        }
      />
    );
  } else if (!fileOptions.quote) {
    preview = (
      <Callout
        intent={Intent.DANGER}
        title={
          copy.components.projectResourceDialog.projectFileUploadPanel
            .validation.quote
        }
      />
    );
  } else if (!fileOptions.escape) {
    preview = (
      <Callout
        intent={Intent.DANGER}
        title={
          copy.components.projectResourceDialog.projectFileUploadPanel
            .validation.escape
        }
      />
    );
  } else if (errors.length) {
    preview = (
      <Callout
        intent={Intent.DANGER}
        title={
          copy.components.projectResourceDialog.projectFileUploadPanel.errors
            .title
        }
      >
        <AdvancedOptions
          text={() =>
            copy.components.projectResourceDialog.projectFileUploadPanel.errors
              .details
          }
        >
          {errors.map((error, idx) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={idx}>
              {
                copy.components.projectResourceDialog.projectFileUploadPanel
                  .errors.row
              }{' '}
              {error.row} - {error.message}
            </div>
          ))}
        </AdvancedOptions>
      </Callout>
    );
  } else if (rows.length) {
    visibleRows = rows.slice(0, MAX_CSV_VISIBLE_LINES);
    preview = (
      <>
        {warningText ? (
          <Callout
            className="sd-project-file-upload-panel__preview__mapping-warning"
            intent={Intent.WARNING}
            title={
              copy.components.projectResourceDialog.projectFileUploadPanel
                .warningTitle
            }
          />
        ) : null}
        <ProjectFileUploadTable
          fields={fields}
          onFieldLabelChange={updateFieldLabel}
          rows={visibleRows}
        />
      </>
    );
  } else if (filePath) {
    preview = (
      <Callout
        intent={Intent.WARNING}
        title={
          copy.components.projectResourceDialog.projectFileUploadPanel.errors
            .empty
        }
      />
    );
  }

  return (
    <div className="sd-project-file-upload-panel">
      <div className="sd-project-file-upload-panel__scroll-wrapper">
        <FormGroup
          inline
          label={
            copy.components.projectResourceDialog.projectFileUploadPanel.form
              .path
          }
          labelFor="sd-project-file-upload-panel-path"
        >
          <InputGroup
            disabled
            fill
            id="sd-project-file-upload-panel-path"
            name="path"
            value={filePath}
          />
          <Link onClick={open}>
            <Text>
              {
                copy.components.projectResourceDialog.projectFileUploadPanel
                  .form.replace
              }
            </Text>
          </Link>
        </FormGroup>
        <FormGroup
          inline
          label={
            copy.components.projectResourceDialog.projectFileUploadPanel.form
              .name
          }
          labelFor="sd-project-file-upload-panel-name"
        >
          <InputGroup
            fill
            id="sd-project-file-upload-panel-name"
            name="name"
            onChange={(evt: React.ChangeEvent<HTMLInputElement>) => {
              setFileName(evt.target.value);
            }}
            value={fileName}
          />
        </FormGroup>
        <div
          className={classNames(
            'sd-project-file-upload-panel__resource-properties',
            Classes.CALLOUT
          )}
        >
          <H5>
            {
              copy.components.projectResourceDialog.projectFileUploadPanel.form
                .resourceProperties
            }
          </H5>
          <ProjectFileUploadOptionsForm
            disableHeaderToggle={isThereDuplicateHeaders}
            fileOptions={fileOptions}
            onSetFileOptions={setFileOptions}
          />
        </div>
        <div
          className={classNames(
            'sd-project-file-upload-panel__preview',
            Classes.CALLOUT
          )}
        >
          <div className="sd-project-file-upload-panel__preview__header">
            <H5>
              {
                copy.components.projectResourceDialog.projectFileUploadPanel
                  .form.preview
              }
            </H5>
            <div data-testid={TEST_IDS.projectFileUploadPanel.rowCount}>
              {visibleRows.length > 0
                ? copy.components.projectResourceDialog.projectFileUploadPanel.form.previewRowCount(
                    visibleRows.length,
                    !isFilePartial && visibleRows.length === rows.length
                  )
                : ''}
            </div>
          </div>
          {preview}
        </div>
      </div>
      <input
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...getInputProps()}
        data-testid={TEST_IDS.home.importProject.fileInput}
      />
    </div>
  );
};
